/**
 * The top bar's rolling messages.
 *
 * The roll itself is CSS, so it runs without this script. The script adds:
 * - Pace: one loop lasts the width of one copy of the list over the chosen
 *   speed, so a long list rolls as fast as a short one. It is measured again
 *   whenever that width changes: fonts arriving, a resize, the editor.
 * - Pause: the button stops the roll and keeps it stopped for the rest of
 *   the visit, on every page. Pointing at the messages or focusing a link in
 *   them stops it too, in CSS.
 * - Editor: selecting a message in the theme editor holds the roll while it
 *   is selected, so it can be read.
 */

/** Pixels per second for each speed setting. */
const SPEEDS = { slow: 30, normal: 50, fast: 80 };
const STORAGE_KEY = 'snusify-topbar-paused';

/** @returns {boolean} */
function readPaused() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** @param {boolean} paused */
function writePaused(paused) {
  try {
    if (paused) sessionStorage.setItem(STORAGE_KEY, '1');
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be off; the pause then lasts for this page only.
  }
}

class SnusifyTopbar extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.button = this.querySelector('[data-topbar-pause]');
    this.group = this.querySelector('[data-topbar-group]');

    this.button?.addEventListener('click', this.#onToggle);
    this.addEventListener('shopify:block:select', this.#onHold);
    this.addEventListener('shopify:block:deselect', this.#onRelease);

    this.#setPaused(readPaused());

    if (!this.group) return;
    this.#observer = new ResizeObserver(() => this.#pace());
    this.#observer.observe(this.group);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.button?.removeEventListener('click', this.#onToggle);
    this.removeEventListener('shopify:block:select', this.#onHold);
    this.removeEventListener('shopify:block:deselect', this.#onRelease);
  }

  #onToggle = () => {
    const paused = this.button?.getAttribute('aria-pressed') !== 'true';
    this.#setPaused(paused);
    writePaused(paused);
  };

  #onHold = () => this.classList.add('is-held');

  #onRelease = () => this.classList.remove('is-held');

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.classList.toggle('is-paused', paused);
    this.button?.setAttribute('aria-pressed', String(paused));
  }

  #pace() {
    const speed = SPEEDS[this.dataset.speed] ?? SPEEDS.normal;
    const width = this.group?.offsetWidth ?? 0;
    if (width === 0) return;
    this.style.setProperty('--snusify-topbar-duration', `${(width / speed).toFixed(1)}s`);
  }
}

if (!customElements.get('snusify-topbar')) {
  customElements.define('snusify-topbar', SnusifyTopbar);
}
